import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useErrors } from 'Components/Organisms/ErrorsProvider/index.jsx';
import storage from 'Utils/storage.js';
import {
    getContactsExportColumns,
    getContactsExportColumnGroups,
    CONTACTS_DISABLED_COLUMNS,
} from 'Utils/contactsExportColumns.js';
import exportImage from 'assets/export-done.svg';
import Button from 'Components/Atoms/Button/Button.jsx';
import Select from 'Components/Atoms/Select/Select';
import Tabs, { Tab } from 'Components/Molecules/Tabs/Tabs';
import ColumnsSelector from 'Components/Molecules/ColumnsSelector/ColumnsSelector';
import { exportContacts, getExportContacts } from 'Services/invoices.js';

const EXPORT_FORMATS = [
    { value: 'xlsx', label: 'Excel (.xlsx)' },
    { value: 'csv', label: 'CSV' },
    { value: 'json', label: 'JSON' },
    { value: 'html', label: 'HTML' },
];

export default function ContactsExportDrawer(props) {
    const { onClose, params = {}, count = 0 } = props;

    const { t } = useTranslation('contacts');

    const COLUMN_GROUPS = getContactsExportColumnGroups(t);
    const INITIAL_COLUMNS = getContactsExportColumns(t);

    const [columns, setColumns] = useState([]);
    const { showUnknownError } = useErrors();
    const [loading, setLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [format, setFormat] = useState('xlsx');
    const [exportUrl, setExportUrl] = useState('');
    const [activeTab, setActiveTab] = useState('data');

    const activeColumnsCount = columns.filter((c) => c.active).length;

    const generateInitialColumns = () => {
        const storageColumns = storage.getItem('export_columns_contacts_v1');
        if (storageColumns) {
            const storedColumns = JSON.parse(storageColumns);
            const keys = Object.keys(storedColumns);

            const newColumns = INITIAL_COLUMNS.map((c) => {
                const active = CONTACTS_DISABLED_COLUMNS.includes(c.id) ? c.active : (storedColumns[c.id] ?? c.active);
                return { ...c, active };
            }).sort((a, b) => keys.indexOf(a.id) - keys.indexOf(b.id));

            setColumns(newColumns);
        } else {
            setColumns(INITIAL_COLUMNS);
        }
    };

    const generateInitialFormat = () => {
        const storageFormat = storage.getItem('export_format_contacts_v1');
        const formatExists = EXPORT_FORMATS.find((f) => f.value === storageFormat);

        if (formatExists) {
            setFormat(storageFormat);
        } else {
            setFormat('xlsx');
        }
    };

    useEffect(() => {
        generateInitialColumns();
        generateInitialFormat();
    }, []);

    const handleExportContacts = async () => {
        if (loading) return;
        setLoading(true);

        const objectColumns = columns.reduce((acc, c) => ({ ...acc, [c.id]: c.active }), {});
        storage.setItem('export_columns_contacts_v1', JSON.stringify(objectColumns));
        storage.setItem('export_format_contacts_v1', format);

        try {
            const exportColumns = columns.filter((c) => c.active).map((c) => c.id);
            const exportParams = {
                ...params,
                export_format: format,
                export_columns: exportColumns,
            };

            await exportContacts(exportParams);
            setLoading(false);
        } catch (error) {
            if (error.response?.data?.description === 'requires_processing') {
                setTimeout(() => exportContactsCheckStatus(error.response.data.info.token), 3000);
            } else {
                reset();
                showUnknownError(error);
            }
        }
    };

    const exportContactsCheckStatus = async (exportId) => {
        getExportContacts(exportId, format).then((body) => {
            if (!body) {
                reset();
                return;
            }

            if (body.url) {
                setExportUrl(body.url);
                setLoading(false);
                setShowSuccess(true);
            } else {
                setTimeout(() => exportContactsCheckStatus(exportId), 3000);
            }
        });
    };

    const reset = () => {
        setShowSuccess(false);
        setExportUrl('');
        setLoading(false);
        setActiveTab('data');
        generateInitialColumns();
        generateInitialFormat();
        onClose();
    };

    const formatSelected = EXPORT_FORMATS.find((f) => f.value === format)?.label || '';

    if (showSuccess) {
        return (
            <div className="mx-auto mt-20 max-w-sm text-center">
                <div className="flex justify-center mb-6">
                    <img src={exportImage} width={75} height={74} alt="" />
                </div>

                <div className="mb-6">
                    <h4 className="mt-6">{t('export-success')}</h4>
                    <div className="mt-2 text-gray-500">
                        {t('export-success-description', { format: formatSelected })}
                    </div>
                </div>

                <Button variant="primary" href={exportUrl} target="_blank" rel="noreferrer" download>
                    {t('download')} {formatSelected}
                </Button>

                <div className="text-xs mt-4 text-gray-500">{t('link-expiration')}</div>
            </div>
        );
    }

    return (
        <div className="grid gap-4 grid-rows-[auto_1fr_auto] absolute inset-0">
            <div>
                <div className="mb-4">
                    <div className="text-sm text-gray-500">{t('export-description', { count })}</div>
                </div>

                <Tabs value={activeTab} onChange={setActiveTab}>
                    <Tab value="data">{t('data')}</Tab>
                    <Tab value="columns">
                        {t('columns')} ({activeColumnsCount}/{columns.length})
                    </Tab>
                </Tabs>
            </div>

            {activeTab === 'data' && (
                <div className="py-3">
                    <Select
                        options={EXPORT_FORMATS}
                        value={format}
                        label={t('format')}
                        onChange={(e) => setFormat(e)}
                        className="w-full mb-6"
                    />
                </div>
            )}

            {activeTab === 'columns' && (
                <ColumnsSelector columns={columns} onColumnsChange={setColumns} groups={COLUMN_GROUPS} />
            )}

            <div className="sticky bottom-0 bg-white pb-4 z-10">
                <div className="flex gap-2">
                    <Button variant="primary" isBusy={loading} onClick={handleExportContacts} className="grow">
                        {t('export')}
                    </Button>
                    <Button variant="default" className="grow" onClick={reset}>
                        {t('cancel')}
                    </Button>
                </div>
            </div>
        </div>
    );
}

ContactsExportDrawer.propTypes = {
    onClose: PropTypes.func.isRequired,
    params: PropTypes.object,
    count: PropTypes.number,
};
